import { snakeCase } from 'change-case'
import _camelCase from 'lodash.camelcase'
import queryString from 'query-string'
import * as Sentry from '@sentry/browser'
import { formatDate as momentFormatDate, DATE_SHORT, DATE_TIME_FULL, DATE_TIME_COMPACT } from './utils/dateHelpers'
import config from './config'

export const UPRIGHT_EPOCH: Date = new Date('2018-03-15T00:01:00.000Z')

export function toTitleCase(str: string): string {
  if (!str) return str
  return str.replace(/_|-/g, ' ').replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
}

export function camelToTitle(text: string): string {
  const result = text.replace(/([A-Z])/g, ' $1')
  return result.charAt(0).toUpperCase() + result.slice(1)
}

const idFieldRegex = /(.*)(Id|ID)$/
export function sanitizedApiField(field: string): string {
  if (!field) return ''

  const matches = field.match(idFieldRegex)
  const adjustedFieldName = matches && matches[1] && matches[1].trim()

  return camelToTitle(adjustedFieldName || field)
}

export function camelToSnake(text: string): string {
  const result = text.replace(/([A-Z])/g, ' $1')
  return snakeCase(result)
}

export function snakeCaseKeys(obj: object): object {
  return Object.keys(obj).reduce((acc, key) => (obj[key] == null ? acc : { ...acc, [camelToSnake(key)]: obj[key] }), {})
}

export function ucFirst(txt: string): string {
  if (!txt) return txt
  return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
}

export function isEquivalent(a: object, b: object): boolean {
  // Create arrays of property names
  const aProps = Object.getOwnPropertyNames(a)
  const bProps = Object.getOwnPropertyNames(b)

  // If number of properties is different,
  // objects are not equivalent
  if (aProps.length !== bProps.length) {
    return false
  }

  for (let i = 0; i < aProps.length; i += 1) {
    const propName = aProps[i]

    // If values of same property are not equal,
    // objects are not equivalent
    if (a[propName] !== b[propName]) {
      return false
    }
  }

  // If we made it this far, objects
  // are considered equivalent
  return true
}

export function formatDimensions(length: number, width: number, height: number): string {
  return [length, width, height].filter(d => d).join('/')
}

export function formatDate(date: Date, full?: boolean, humanized = false): string {
  if (!date) return null
  let pattern = DATE_SHORT
  if (full) pattern = DATE_TIME_FULL
  else if (humanized) pattern = DATE_TIME_COMPACT
  return momentFormatDate(date, pattern)
}

export async function copyToClipboard(textValue: string) {
  try {
    return navigator.clipboard.writeText(textValue)
  } catch (error) {
    Sentry.captureException(error)
    return undefined
  }
}

interface StatusMap {
  [key: string]: string
}
export const STATUS_MAP: StatusMap = {
  PENDING: 'bp5-intent-warning',
  LISTING: 'bp5-intent-warning',
  DELISTING: 'bp5-intent-warning',
  MODIFYING: 'bp5-intent-warning',
  LISTED: 'bp5-intent-success',
  DELISTED: 'bp5-intent-primary',
  LISTING_FAILED: 'bp5-intent-danger',
  MODIFY_FAILED: 'bp5-intent-danger',
  DELISTING_FAILED: 'bp5-intent-danger',
  MIXED: 'bp5-intent-warning',
  SOLD: 'bp5-intent-success',
  EXPIRED: 'bp5-intent-primary'
}

export function pushQueryParams(title: any, meta?: any): any {
  window.history.pushState(title, title, `${window.location.pathname}?${queryString.stringify(meta)}`)
}

export const mapToLabelValueList = (constant: object): any =>
  Object.entries(constant).map(s => ({ value: s[0], label: s[1].humanReadableName || s[1] }))

export function readQueryParams(): any {
  return queryString.parse(window.location.search)
}

export const randInt = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min

export const getSort = (sort: string): any => {
  if (!sort) {
    return [{}]
  }

  const parts = sort.split('.')

  return [
    {
      id: parts[0],
      desc: parts[1] === 'desc'
    }
  ]
}

export function stripExtraSpace(str: string): string {
  return str.replace(/\s+/g, ' ').trim()
}

export function sanitizeOperationalSku(value: string): string {
  return value.replace(/[^a-zA-Z0-9-]/g, '')
}

export const SOLUTIONS_QR_CODE_START_SYMBOL = '@@EMBEDDED@@'

export function isSolutionsQRCodePayload(value: string): boolean {
  return value.startsWith(SOLUTIONS_QR_CODE_START_SYMBOL)
}

// Make sure func has a stable identity (i.e. declare it outside of a functional component, or memoize it with useCallback).
// You don't want `func` to change based on parameters it needs, so make sure to pass data in as function parameters instead of referencing vars in the surrounding closure.
//
// Good example:
// const func = (query) => Client.getProducts(query) // option 1
// const MyComponent: props => {
//   const func = (query) => React.useCallback(Client.getProducts(query), []) // option 2
//   const debouncedFunc = debounce(func, 500)
//   debouncedFunc(props.query) // inside some useEffect
//
// Bad example:
// const MyComponent: props => {
//   const func = () => React.useCallback(Client.getProducts(props.query), [props.query])
//   const debouncedFunc = debounce(func, 500)
//   debouncedFunc(props.query) // inside some useEffect
export function debounce(func: any, wait: number, immediate?: boolean): any {
  /* eslint-disable */
  let timeout
  return function () {
    const context = this

    const args = arguments
    const later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
  /* eslint-enable */
}

export function randomStr(len = 10): string {
  let s = ''
  const r = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  for (let i = 0; i < len; i += 1) {
    s += r.charAt(Math.floor(Math.random() * r.length))
  }

  if (config.orgConfig.name === 'Goodwill Industries of the Inland Northwest') {
    const ld = randInt(0, 99)
    return s.slice(0, s.length - ld.toString().length) + ld
  }

  return s
}

export function formatMoney(
  amount: any,
  decimalCount = 2,
  decimal = '.',
  thousands = ',',
  currency = '$'
): string | null {
  let num = amount
  if (typeof amount === 'string') {
    num = parseFloat(amount)
  }

  try {
    const negativeSign = num < 0 ? '-' : ''

    const i = parseInt((num = Math.abs(Number(num) || 0).toFixed(decimalCount))).toString()
    const j = i.length > 3 ? i.length % 3 : 0

    return `${currency}${
      negativeSign +
      (j ? i.substr(0, j) + thousands : '') +
      i.substr(j).replace(/(\d{3})(?=\d)/g, `$1${thousands}`) +
      (decimalCount
        ? decimal +
          Math.abs(num - Number(i))
            .toFixed(decimalCount)
            .slice(2)
        : '')
    }`
  } catch (_e) {
    return null
  }
}

export const formatMoneyFromCurrencyCode = (
  amount: any,
  decimalCount = 2,
  decimal = '.',
  thousands = ',',
  currencyCode: any
): string | null => {
  let currency
  switch (currencyCode) {
    case 'USD':
      currency = '$'
      break
    case 'CAD':
      currency = 'C$'
      break
    default:
      currency = '$'
  }
  return formatMoney(amount, decimalCount, decimal, thousands, currency)
}

export const formatMoneyWithCurrency = (amount: any, currencyCode: any): any => {
  return formatMoneyFromCurrencyCode(amount, 2, '.', ',', currencyCode)
}

// Format "8" as "8.00", "3.5" as "3.50", etc.
export const formatMoneyForInput = (amount: string) => {
  if (!amount) {
    return amount
  }

  return Number(amount).toFixed(2)
}

export function sortAlpha(options: any[], key: string): any {
  return options.sort((a, b) => a[key].localeCompare(b[key]))
}

// shape: {"errors":{"default_end_time":["can't be blank"]}}
export function humanReadableErrors(err: any): any {
  return Object.keys(err.errors)
    .map(e => `${camelToTitle(e)} ${err.errors[e].join(', ')}`)
    .join('\n')
}

/** Flatten API error payloads (string, array, or nested objects) for display. */
export function formatApiErrorDetail(value: unknown): string | undefined {
  if (value == null) return undefined
  if (Array.isArray(value)) {
    const parts = value.map(item => formatApiErrorDetail(item) ?? String(item)).filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : undefined
  }
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    const { message } = value as { message?: unknown }
    if (typeof message === 'string') return message
    return JSON.stringify(value)
  }
  return String(value)
}

export function apiErrorDetailFromResponseData(data: Record<string, unknown> | undefined): string | undefined {
  if (!data) return undefined
  return formatApiErrorDetail(data.errors) ?? formatApiErrorDetail(data.message) ?? formatApiErrorDetail(data.error)
}

export function prefixedApiErrorMessage(
  prefix: string,
  err: { response?: { data?: Record<string, unknown> } }
): string {
  const detail = apiErrorDetailFromResponseData(err.response?.data)
  return detail ? `${prefix}: ${detail}. Please try again.` : `${prefix}. Please try again.`
}

export function categoryMarketCategoryToPath(cmc: any): any {
  if (!cmc) return []

  return (cmc.ancestry || '')
    .split('/')
    .map(i => parseInt(i))
    .filter(i => !Number.isNaN(i))
    .concat(cmc.id)
    .filter(i => i)
}

export function niceNumber(n: number): string {
  if (!n) return null
  const parts = n.toString().split('.')
  return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (parts[1] ? `.${parts[1]}` : '')
}

/**
 * @deprecated Use the pluralize utility function instead.
 */
export const S = (items: any): string => {
  if (!items) return ''
  const len = items.length

  if (len === 0) {
    return 's'
  }

  return len > 1 ? 's' : ''
}

// utc to local time: '12:00' => '07:00'
export function utcStringToLocalString(scheduleTime: any): string {
  if (!scheduleTime) return null
  const scheduleTimeArray = scheduleTime.split(':')
  const date = new Date()
  date.setUTCHours(scheduleTimeArray[0])
  date.setUTCMinutes(scheduleTimeArray[1])
  const hours = date.getHours()
  const mins = date.getMinutes()
  return `${hours < 10 ? `0${hours}` : hours}:${mins < 10 ? `0${mins}` : mins}`
}

// local time to utc: '07:00' => '12:00'
export function localStringToUtcString(scheduleTime: any): string {
  if (!scheduleTime) return null
  const scheduleTimeArray = scheduleTime.split(':')
  const date = new Date()
  date.setHours(scheduleTimeArray[0])
  date.setMinutes(scheduleTimeArray[1])
  const hours = date.getUTCHours()
  const mins = date.getUTCMinutes()
  return `${hours < 10 ? `0${hours}` : hours}:${mins < 10 ? `0${mins}` : mins}`
}

export function safeTrim(str: any): any {
  if (typeof str !== 'string') return str

  return str.trim()
}

export function toPercentage(num: number, decimalPlaces = 1): string {
  if (num === null || num === undefined) return null
  return `${(num * 100).toFixed(decimalPlaces)}%`
}

export function camelize(str: string): string {
  return _camelCase(str)
}
